import { useMemo } from 'react';
import { useGetMachinesQuery } from '../../gql/generated';
import { useAlarmFilters } from '../../hooks/useAlarmFilters';
import { FilterTag } from './ActiveFilterTags';

/**
 * Hook that returns active filter tags for alarm filters.
 * Provides tag objects compatible with the ActiveFilterTags component.
 */
export function useAlarmFilterTags(): FilterTag[] {
  const { filters, setName, setMachines, setSignals } = useAlarmFilters();

  const { data: machinesData } = useGetMachinesQuery({
    variables: {
      pagination: {
        limit: 1000,
        offset: 0,
      },
    },
    fetchPolicy: 'cache-and-network',
  });

  const machineOptions = useMemo(
    () =>
      (machinesData?.machines.rows || []).map((machine) => ({
        value: machine.machineId,
        label: machine.displayName || machine.machineId,
      })),
    [machinesData]
  );

  const activeFilterTags = useMemo(() => {
    const tags: FilterTag[] = [];

    if (filters.name) {
      tags.push({
        key: 'name',
        label: `Name: ${filters.name}`,
        onRemove: () => setName(''),
      });
    }

    filters.machines.forEach((id) => {
      const option = machineOptions.find((machine) => machine.value === id);
      tags.push({
        key: `machine-${id}`,
        label: option?.label || id,
        onRemove: () =>
          setMachines(filters.machines.filter((machineId) => machineId !== id)),
      });
    });

    filters.signals.forEach((value) => {
      tags.push({
        key: `signal-${value}`,
        label: value,
        onRemove: () =>
          setSignals(filters.signals.filter((signal) => signal !== value)),
      });
    });

    return tags;
  }, [filters, machineOptions, setMachines, setName, setSignals]);

  return activeFilterTags;
}

